import React, { useState, useEffect } from 'react';
import {
  Tag,
  TrendingUp,
  LayoutGrid,
  BarChart2,
  ArrowLeftRight,
  LineChart,
  PieChart as PieChartIcon,
  Loader2,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { advancedReportsAPI } from '../../services/api';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

interface LabelAnalyticsProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
  selectedAgent?: number | 'all';
  selectedChannel?: number | 'all';
}

interface LabelBreakdown {
  label_id: number;
  label_title: string;
  label_color: string;
  conversation_count: number;
  percentage: number;
  avg_resolution_time: number;
  avg_csat: number;
  resolution_rate: number;
  resolved_count: number;
}

interface LabelTrend {
  date: string;
  total_conversations: number;
  labeled_conversations: number;
  label_counts: { [key: string]: number };
}

interface LabelPerformance {
  label_title: string;
  label_color: string;
  conversation_count: number;
  avg_resolution_time: number;
  avg_csat: number;
  resolution_rate: number;
}

interface LabelCombination {
  label_1: string;
  label_1_color: string;
  label_2: string;
  label_2_color: string;
  count: number;
}

interface LabelByChannel {
  channel: string;
  labels: { label: string; count: number }[];
}

interface LabelByAgent {
  agent: string;
  total_labeled_conversations: number;
  labels: { label: string; count: number; percentage: number }[];
}

interface TopLabel {
  id: number;
  label: string;
  value: number;
  color: string;
}

interface LabelAnalyticsData {
  summary: {
    total_labels: number;
    total_labeled_conversations: number;
    total_conversations: number;
    avg_labels_per_conversation: number;
    unlabeled_conversations: number;
    labeling_rate: number;
    most_used_label: { title: string; count: number; color: string } | null;
    least_used_label: { title: string; count: number; color: string } | null;
  };
  labels_breakdown: LabelBreakdown[];
  label_trend: LabelTrend[];
  label_performance: LabelPerformance[];
  label_combinations: LabelCombination[];
  label_by_channel: LabelByChannel[];
  label_by_agent: LabelByAgent[];
  top_labels: TopLabel[];
}

// Preserves user-configured label colors from API
const LabelColorBadge: React.FC<{ title: string; color: string }> = ({ title, color }) => (
  <span
    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
    style={{ backgroundColor: color }}
  >
    {title}
  </span>
);

const getCsatVariant = (csat: number): 'secondary' | 'outline' | 'destructive' => {
  if (csat >= 4) return 'secondary';
  if (csat >= 3) return 'outline';
  return 'destructive';
};

// Fallback colors for pie slices that don't have an API color
const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#14b8a6'];

export const LabelAnalytics: React.FC<LabelAnalyticsProps> = ({
  dateRange,
  selectedAgent,
  selectedChannel,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyticsData, setAnalyticsData] = useState<LabelAnalyticsData | null>(null);

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange, selectedAgent, selectedChannel]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date:   dateRange.endDate.toISOString().split('T')[0],
      };

      if (selectedAgent && selectedAgent !== 'all') params.agent_id = selectedAgent;
      if (selectedChannel && selectedChannel !== 'all') params.channel_id = selectedChannel;

      const data = await advancedReportsAPI.getLabelAnalytics(params);
      setAnalyticsData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch label analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="m-2">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!analyticsData) {
    return (
      <Alert className="m-2">
        <AlertDescription>No label analytics data available for the selected period.</AlertDescription>
      </Alert>
    );
  }

  const {
    summary,
    labels_breakdown,
    label_trend,
    label_performance,
    label_combinations,
    label_by_channel,
    label_by_agent,
    top_labels,
  } = analyticsData;

  const trendChartData = label_trend.map((item) => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    total:   item.total_conversations,
    labeled: item.labeled_conversations,
  }));

  return (
    <div className="space-y-5">

      {/* �??�?? KPI stat strip �??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�?? */}
      <div className="flex flex-wrap divide-x divide-border rounded-lg border border-border/60 bg-muted/10 overflow-hidden">
        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[130px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Tag className="h-3.5 w-3.5" />Total Labels
          </span>
          <span className="text-lg font-semibold tabular-nums mt-1">
            {summary.total_labels}
          </span>
        </div>

        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[160px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <TrendingUp className="h-3.5 w-3.5" />Labeling Rate
          </span>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-lg font-semibold tabular-nums">{summary.labeling_rate}%</span>
            <span className="text-xs text-muted-foreground">
              {summary.total_labeled_conversations} / {summary.total_conversations}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[130px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <LayoutGrid className="h-3.5 w-3.5" />Avg Labels / Conv
          </span>
          <span className="text-lg font-semibold tabular-nums mt-1">
            {summary.avg_labels_per_conversation}
          </span>
        </div>

        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[130px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <BarChart2 className="h-3.5 w-3.5" />Unlabeled
          </span>
          <span className="text-lg font-semibold tabular-nums mt-1">
            {summary.unlabeled_conversations}
          </span>
        </div>
      </div>

      {/* �??�?? Most / Least used �?? 2-slot strip �??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�?? */}
      <div className="flex flex-wrap divide-x divide-border rounded-lg border border-border/60 overflow-hidden">
        <div className="flex flex-col gap-2 px-5 py-4 flex-1">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <TrendingUp className="h-3.5 w-3.5" />Most Used Label
          </span>
          {summary.most_used_label ? (
            <div className="flex items-center gap-3">
              <LabelColorBadge
                title={summary.most_used_label.title}
                color={summary.most_used_label.color}
              />
              <span className="text-sm font-semibold tabular-nums">
                {summary.most_used_label.count.toLocaleString()}
              </span>
              <span className="text-xs text-muted-foreground">conversations</span>
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">No labels used yet</span>
          )}
        </div>

        <div className="flex flex-col gap-2 px-5 py-4 flex-1">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <BarChart2 className="h-3.5 w-3.5" />Least Used Label
          </span>
          {summary.least_used_label ? (
            <div className="flex items-center gap-3">
              <LabelColorBadge
                title={summary.least_used_label.title}
                color={summary.least_used_label.color}
              />
              <span className="text-sm font-semibold tabular-nums">
                {summary.least_used_label.count.toLocaleString()}
              </span>
              <span className="text-xs text-muted-foreground">conversations</span>
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">No labels used yet</span>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Label Distribution �?? Pie */}
        <div className="rounded-lg border border-border/60 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
            <PieChartIcon className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm font-medium">Label Distribution</span>
          </div>
          <div className="p-4">
            {top_labels.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={top_labels}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    nameKey="label"
                    label={({ name, percent }) =>
                      `${name} (${(percent !== undefined ? percent * 100 : 0).toFixed(0)}%)`
                    }
                    outerRadius={90}
                    dataKey="value"
                  >
                    {top_labels.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.color || COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[260px] text-sm text-muted-foreground">
                No label data available
              </div>
            )}
          </div>
        </div>

        {/* Labeling Trend �?? Area */}
        <div className="rounded-lg border border-border/60 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
            <LineChart className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm font-medium">Labeling Trend</span>
          </div>
          <div className="p-4">
            {trendChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={trendChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <RechartsTooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area
                    type="monotone"
                    dataKey="total"
                    stackId="1"
                    stroke="#3b82f6"
                    fill="#3b82f620"
                    name="Total"
                  />
                  <Area
                    type="monotone"
                    dataKey="labeled"
                    stackId="2"
                    stroke="#22c55e"
                    fill="#22c55e20"
                    name="Labeled"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[260px] text-sm text-muted-foreground">
                No trend data available
              </div>
            )}
          </div>
        </div>

      </div>

      {/* �??�?? Label Performance Table �??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�?? */}
      <div className="rounded-lg border border-border/60 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
          <BarChart2 className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm font-medium">Label Performance Metrics</span>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Label</TableHead>
              <TableHead className="text-right">Conversations</TableHead>
              <TableHead className="text-right">Avg Resolution</TableHead>
              <TableHead>Resolution Rate</TableHead>
              <TableHead className="text-right">Avg CSAT</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {label_performance.length > 0
              ? label_performance.map((label, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <LabelColorBadge title={label.label_title} color={label.label_color} />
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {label.conversation_count}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                      {label.avg_resolution_time > 0
                        ? `${Math.round(label.avg_resolution_time)} min`
                        : '�??'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={label.resolution_rate} className="h-1.5 w-16" />
                        <span className="text-xs tabular-nums text-muted-foreground w-8 shrink-0">
                          {label.resolution_rate}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {label.avg_csat > 0 ? (
                        <Badge variant={getCsatVariant(label.avg_csat)} className="tabular-nums">
                          {label.avg_csat.toFixed(1)}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">�??</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              : (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                    No label performance data available
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
      </div>

      {/* �??�?? Combinations + By Channel �??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�?? */}
      <div className="grid gap-4 md:grid-cols-2">

        {/* Common Label Combinations */}
        <div className="rounded-lg border border-border/60 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
            <ArrowLeftRight className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm font-medium">Common Label Combinations</span>
          </div>
          {label_combinations.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Label Pair</TableHead>
                  <TableHead className="text-right w-20">Count</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {label_combinations.map((combo, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <LabelColorBadge title={combo.label_1} color={combo.label_1_color} />
                        <span className="text-xs text-muted-foreground">+</span>
                        <LabelColorBadge title={combo.label_2} color={combo.label_2_color} />
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{combo.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No combinations found
            </div>
          )}
        </div>

        {/* Labels by Channel */}
        <div className="rounded-lg border border-border/60 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
            <LayoutGrid className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm font-medium">Labels by Channel</span>
          </div>
          {label_by_channel.length > 0 ? (
            <div className="divide-y divide-border/40">
              {label_by_channel.map((channel, index) => (
                <div key={index} className="px-4 py-3">
                  <p className="text-sm font-medium capitalize mb-2">{channel.channel}</p>
                  <div className="flex flex-wrap gap-1">
                    {channel.labels.map((label, idx) => (
                      <span
                        key={idx}
                        title={`${label.count} conversations`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted text-xs text-muted-foreground"
                      >
                        {label.label}
                        <span className="font-semibold text-foreground">{label.count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No channel data available
            </div>
          )}
        </div>

      </div>

      {/* �??�?? Label Usage by Agent �??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�?? */}
      <div className="rounded-lg border border-border/60 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
          <BarChart2 className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm font-medium">Label Usage by Agent</span>
        </div>
        {label_by_agent.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Agent</TableHead>
                <TableHead className="text-right w-44">Labeled Conversations</TableHead>
                <TableHead>Top Labels</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {label_by_agent.slice(0, 10).map((agent, index) => (
                <TableRow key={index}>
                  <TableCell className="text-sm">{agent.agent}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {agent.total_labeled_conversations}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {agent.labels.slice(0, 3).map((label, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted text-xs text-muted-foreground"
                        >
                          {label.label}
                          <span className="font-semibold text-foreground">{label.percentage}%</span>
                        </span>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No agent label data available
          </div>
        )}
      </div>

      {/* �??�?? Complete Label Breakdown �??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�?? */}
      <div className="rounded-lg border border-border/60 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
          <Tag className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm font-medium">Complete Label Breakdown</span>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Label</TableHead>
              <TableHead className="text-right">Conversations</TableHead>
              <TableHead className="text-right">% of Total</TableHead>
              <TableHead>Resolution Rate</TableHead>
              <TableHead className="text-right">Avg Resolution</TableHead>
              <TableHead className="text-right">Avg CSAT</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {labels_breakdown.length > 0
              ? labels_breakdown.map((label, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <LabelColorBadge title={label.label_title} color={label.label_color} />
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {label.conversation_count}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                      {label.percentage}%
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={label.resolution_rate} className="h-1.5 w-16" />
                        <span className="text-xs tabular-nums text-muted-foreground w-8 shrink-0">
                          {label.resolution_rate}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                      {label.avg_resolution_time > 0
                        ? `${Math.round(label.avg_resolution_time)} min`
                        : '�??'}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {label.avg_csat > 0 ? label.avg_csat.toFixed(1) : '�??'}
                    </TableCell>
                  </TableRow>
                ))
              : (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                    No label data available
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
      </div>

    </div>
  );
};

export default LabelAnalytics;
